import { useScrollFade } from '@databases/action-bar/useScrollFade';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isMobile, isNativeEmbedEditorPlugin } from '@databases/modes';
import { type PanelsType } from '@databases/panel/PanelContext';
import usePanelState from '@databases/panel/usePanelState';
import type React from 'react';
import { useSnapshot } from 'valtio';

import { currentThemeState } from '@canvas/canvas-state/theme';

const OVERFLOW_SHADOW_GRADIENT_END_LIGHT = 'rgba(9, 30, 66, 0.08)';
const OVERFLOW_SHADOW_GRADIENT_END_DARK = 'rgba(1, 4, 4, 0.56)';

const getOverflowShadowGradientEndForTheme = (theme: string): string =>
  theme === 'dark'
    ? OVERFLOW_SHADOW_GRADIENT_END_DARK
    : OVERFLOW_SHADOW_GRADIENT_END_LIGHT;

const getOverflowShadowTopOffset = (): number => {
  if (isMobile()) {
    return 110;
  }
  if (isNativeEmbedEditorPlugin()) {
    return 40;
  }
  return 50;
};

type OverFlowShadowData = {
  enableOverflowShadow: boolean;
  topOffset: number;
  getOverflowShadowGradientStyle: () => React.CSSProperties;
};

export const useOverflowShadow = ({
  isConfluencePanelOpen,
}: {
  isConfluencePanelOpen: boolean;
}): OverFlowShadowData => {
  const { theme } = useSnapshot(currentThemeState);
  const { isPanelVisible } = usePanelState();
  const panels: Array<keyof PanelsType> = [
    'FieldSettingsPanel',
    'ViewSettingsPanel',
    'EntryViewPanel',
    'DatabaseSetupPanel',
  ];
  const isAnyPanelOpen = panels.some((panel) => isPanelVisible(panel));
  const { referencing } = useDatabaseContent();
  const versionId = getVersionId();
  const showRightOverflowShadow = useScrollFade('scrollable-inner-container');
  const topOffset = getOverflowShadowTopOffset();

  const enableOverflowShadow =
    showRightOverflowShadow &&
    !isAnyPanelOpen &&
    !referencing &&
    !isConfluencePanelOpen &&
    !versionId;

  const getOverflowShadowGradientStyleForTheme = (): React.CSSProperties => ({
    background: `linear-gradient(to right, transparent, ${getOverflowShadowGradientEndForTheme(
      theme
    )})`,
  });

  return {
    enableOverflowShadow,
    topOffset,
    getOverflowShadowGradientStyle: getOverflowShadowGradientStyleForTheme,
  };
};
